import { Container, Graphics, Text } from 'pixi.js';
import type { ScenarioChoice } from '../app/types';
import { Button } from './Button';
import { createTextLayoutDebugEntry, type TextLayoutDebugEntry } from './TextLayoutDebug';
import { UI_COLORS, UI_TEXT_STYLES } from './Theme';

export interface ChoicePanelOptions {
  width?: number;
  choiceHeight?: number;
  gap?: number;
  unlockDelayMs?: number;
  onSelect?: (choice: ScenarioChoice, index: number) => void;
}

export interface ChoicePanelState {
  visible: boolean;
  locked: boolean;
  selectedChoiceId: string | null;
  choiceCount: number;
  introComplete: boolean;
  unlockRemainingMs: number;
}

const DEFAULT_UNLOCK_DELAY_MS = 260;
const INTRO_DURATION_MS = 260;
const INTRO_STAGGER_MS = 46;
const CHOICE_SLIDE_OFFSET = 22;
const HEADER_HEIGHT = 48;
const HEADER_PLATE_WIDTH = 154;
const HEADER_PLATE_HEIGHT = 40;
const PANEL_PADDING = 10;

function cloneChoice(choice: ScenarioChoice): ScenarioChoice {
  return {
    ...choice,
    actions: choice.actions?.map((action) => ({ ...action }))
  };
}

function normalizeDelayMs(delayMs: number): number {
  return Number.isFinite(delayMs) ? Math.max(0, delayMs) : DEFAULT_UNLOCK_DELAY_MS;
}

export class ChoicePanel extends Container {
  private readonly backdrop = new Graphics();
  private readonly headerPlate = new Graphics();
  private readonly headingText = new Text({
    text: '推理選択',
    style: UI_TEXT_STYLES.panelTitle
  });
  private readonly topLine = new Graphics();
  private readonly choiceLayer = new Container();
  private readonly buttons: Button[] = [];

  private panelWidth: number;
  private choiceHeight: number;
  private gap: number;
  private unlockDelayMs: number;
  private unlockTimerMs = 0;
  private introElapsedMs = 0;
  private locked = true;
  private choices: ScenarioChoice[] = [];
  private selectedChoiceId: string | null = null;
  private onSelect?: (choice: ScenarioChoice, index: number) => void;

  public constructor(options: ChoicePanelOptions = {}) {
    super();
    this.panelWidth = options.width ?? 720;
    this.choiceHeight = options.choiceHeight ?? 60;
    this.gap = options.gap ?? 14;
    this.unlockDelayMs = normalizeDelayMs(options.unlockDelayMs ?? DEFAULT_UNLOCK_DELAY_MS);
    this.onSelect = options.onSelect;

    this.addChild(this.backdrop);
    this.addChild(this.headerPlate);
    this.addChild(this.headingText);
    this.addChild(this.topLine);
    this.addChild(this.choiceLayer);
    this.visible = false;
    this.drawFrame();
  }

  public setChoices(choices: readonly ScenarioChoice[]): void {
    this.clearButtons();
    this.choices = choices.map(cloneChoice);
    this.selectedChoiceId = null;
    this.locked = true;
    this.introElapsedMs = 0;
    this.alpha = 0;

    this.choices.forEach((choice, index) => {
      const targetY = this.getChoiceTargetY(index);
      const button = new Button({
        label: choice.text,
        width: this.panelWidth,
        height: this.choiceHeight,
        variant: 'secondary',
        disabled: true,
        playPressSound: false,
        onPress: () => this.selectChoice(index)
      });
      button.position.set(0, targetY + CHOICE_SLIDE_OFFSET);
      button.alpha = 0;
      this.buttons.push(button);
      this.choiceLayer.addChild(button);
    });

    this.unlockTimerMs = Math.max(this.unlockDelayMs, this.getIntroTotalMs());
    this.visible = this.choices.length > 0;
    if (this.visible && this.unlockTimerMs <= 0) {
      this.setLocked(false);
    }
    this.layout();
  }

  public update(deltaMs: number): void {
    if (!this.visible || this.choices.length === 0) {
      return;
    }

    const elapsedMs = Number.isFinite(deltaMs) ? Math.max(0, deltaMs) : 0;
    this.updateIntroAnimation(elapsedMs);

    if (!this.locked || this.choices.length === 0) {
      return;
    }

    this.unlockTimerMs -= elapsedMs;
    if (this.unlockTimerMs <= 0) {
      this.setLocked(false);
      this.updateIntroAnimation(0);
    }
  }

  public setOnSelect(
    onSelect: ((choice: ScenarioChoice, index: number) => void) | undefined
  ): void {
    this.onSelect = onSelect;
  }

  public setLocked(locked: boolean): void {
    this.locked = locked;
    for (const button of this.buttons) {
      button.setDisabled(locked || this.selectedChoiceId !== null);
    }
  }

  public hide(): void {
    this.visible = false;
    this.clearButtons();
    this.choices = [];
    this.selectedChoiceId = null;
    this.locked = true;
    this.unlockTimerMs = 0;
    this.introElapsedMs = 0;
    this.alpha = 0;
  }

  public getState(): ChoicePanelState {
    return {
      visible: this.visible,
      locked: this.locked,
      selectedChoiceId: this.selectedChoiceId,
      choiceCount: this.choices.length,
      introComplete: this.isIntroComplete(),
      unlockRemainingMs: Math.max(0, this.unlockTimerMs)
    };
  }

  public getTextLayoutDebugEntries(prefix = 'choicePanel'): TextLayoutDebugEntry[] {
    if (!this.visible) {
      return [];
    }

    return [
      createTextLayoutDebugEntry(`${prefix}.heading`, this.headingText, HEADER_PLATE_WIDTH - 28, 32),
      ...this.buttons.map((button, index) =>
        button.getTextLayoutDebugEntry(
          `${prefix}.choice.${this.choices[index]?.id ?? String(index)}`
        )
      )
    ];
  }

  private selectChoice(index: number): void {
    if (this.locked || this.selectedChoiceId !== null) {
      return;
    }

    const choice = this.choices[index];
    if (!choice) {
      return;
    }

    this.selectedChoiceId = choice.id ?? `${choice.next}:${index}`;
    for (const [buttonIndex, button] of this.buttons.entries()) {
      const selected = buttonIndex === index;
      button.setVariant(selected ? 'primary' : 'ghost');
      button.setDisabled(true, { muted: !selected });
      button.alpha = selected ? 1 : 0.54;
      button.scale.set(selected ? 1.02 : 1);
    }

    this.onSelect?.(cloneChoice(choice), index);
  }

  private clearButtons(): void {
    for (const button of this.buttons.splice(0)) {
      button.destroy({ children: true });
    }
    this.choiceLayer.removeChildren();
  }

  private layout(): void {
    this.headingText.position.set(22, 8);
    this.choiceLayer.position.set(0, HEADER_HEIGHT);
    this.buttons.forEach((button, index) => {
      if (this.introElapsedMs <= 0) {
        button.position.set(0, this.getChoiceTargetY(index) + CHOICE_SLIDE_OFFSET);
      } else if (this.isIntroComplete()) {
        button.position.set(0, this.getChoiceTargetY(index));
      }
    });
    this.drawFrame();
  }

  private drawFrame(): void {
    const panelHeight = this.getPanelHeight();

    this.backdrop.clear();
    this.backdrop
      .roundRect(
        -PANEL_PADDING,
        -PANEL_PADDING,
        this.panelWidth + PANEL_PADDING * 2,
        panelHeight + PANEL_PADDING * 2,
        8
      )
      .fill({ color: UI_COLORS.black, alpha: 0.42 });

    this.headerPlate.clear();
    this.headerPlate
      .roundRect(0, 0, HEADER_PLATE_WIDTH, HEADER_PLATE_HEIGHT, 6)
      .fill({ color: UI_COLORS.black, alpha: 0.88 })
      .stroke({ color: UI_COLORS.gold, width: 2, alpha: 0.74 });
    this.headerPlate
      .rect(11, 8, 4, HEADER_PLATE_HEIGHT - 16)
      .fill({ color: UI_COLORS.gold, alpha: 0.78 });

    this.topLine.clear();
    this.topLine
      .rect(HEADER_PLATE_WIDTH + 14, 21, this.panelWidth - HEADER_PLATE_WIDTH - 14, 2)
      .fill({ color: UI_COLORS.gold, alpha: 0.56 });
  }

  private updateIntroAnimation(deltaMs: number): void {
    this.introElapsedMs = Math.min(this.getIntroTotalMs(), this.introElapsedMs + deltaMs);
    this.alpha = easeOutCubic(Math.min(1, this.introElapsedMs / INTRO_DURATION_MS));

    for (const [index, button] of this.buttons.entries()) {
      const localElapsedMs = this.introElapsedMs - index * INTRO_STAGGER_MS;
      const progress = Math.min(1, Math.max(0, localElapsedMs / INTRO_DURATION_MS));
      const easedProgress = easeOutCubic(progress);
      const targetY = this.getChoiceTargetY(index);
      button.position.set(0, targetY + CHOICE_SLIDE_OFFSET * (1 - easedProgress));
      if (this.selectedChoiceId === null) {
        button.alpha = easedProgress * (this.locked ? 0.86 : 1);
      }
    }
  }

  private isIntroComplete(): boolean {
    return this.introElapsedMs >= this.getIntroTotalMs();
  }

  private getIntroTotalMs(): number {
    return INTRO_DURATION_MS + Math.max(0, this.buttons.length - 1) * INTRO_STAGGER_MS;
  }

  private getChoiceTargetY(index: number): number {
    return index * (this.choiceHeight + this.gap);
  }

  private getPanelHeight(): number {
    if (this.choices.length <= 0) {
      return HEADER_HEIGHT;
    }

    return (
      HEADER_HEIGHT +
      this.choices.length * this.choiceHeight +
      Math.max(0, this.choices.length - 1) * this.gap
    );
  }
}

function easeOutCubic(progress: number): number {
  return 1 - Math.pow(1 - progress, 3);
}
